import { getLogger } from '@logtape/logtape';
import type { ConfigData } from '#lib/apps/types.js';
import { getDataClient, localDatabaseExists } from '#data/safesystem/safecurves/factory.js';
import { activeProgramsVersion, programsEnabled } from './gate';
import {
	buildProgramsMapFeatures,
	type ProgramsMapFeatures,
	type ProjectRows
} from './mapFeatures';

const log = getLogger(['safesystem', 'safecurves', 'programs', 'map']);

const EMPTY: ProgramsMapFeatures = buildProgramsMapFeatures([]);

// The active version's projects plus their existing signs and barriers, for the main SafeCurves map and table.
class ProgramsMapStore {
	features = $state<ProgramsMapFeatures>(EMPTY);
	// Gates the Programs legend rows and table tabs.
	hasProjects = $state(false);
	#loadId = 0;

	// Never creates the local DB: a device that has never opened Programs has nothing to show.
	async load(configData: ConfigData): Promise<void> {
		const loadId = ++this.#loadId;
		const version = activeProgramsVersion();
		let rows: ProjectRows[] = [];
		try {
			if (version && programsEnabled(configData) && (await localDatabaseExists()))
				rows = await readRows(version);
		} catch (error) {
			log.error('Failed to load Programs map data: {error}', { error });
		}
		if (loadId !== this.#loadId) return;
		this.features = buildProgramsMapFeatures(rows);
		this.hasProjects = rows.length > 0;
		log.info('Loaded {count} projects for the map', { count: rows.length });
	}
}

// Every table read once for the whole version, then grouped by project (curves via their segment).
async function readRows(version: string): Promise<ProjectRows[]> {
	const client = await getDataClient();
	const programs = await client.listPrograms(version);
	const projects = (
		await Promise.all(
			programs.map(async (program) =>
				(await client.listProjects(program.id)).map((project) => ({ program, project }))
			)
		)
	).flat();
	if (projects.length === 0) return [];
	const ids = projects.map((p) => p.project.id);
	const [corridors, segments, curves, signs, signApps, barriers, barrierApps] = await Promise.all([
		client.listCorridors(ids),
		client.listSegments(ids),
		client.listProjectCurves(ids),
		client.listProjectSigns(ids),
		client.listCountermeasureApplications(ids, 'sign'),
		client.listProjectBarriers(ids),
		client.listCountermeasureApplications(ids, 'barrier')
	]);
	const projectBySegment = new Map(segments.map((s) => [s.id, s.projectId]));
	const projectByCurve = new Map(curves.map((c) => [c.id, projectBySegment.get(c.segmentId)]));
	const byProject = <T>(rows: T[], projectOf: (row: T) => string | undefined) =>
		Map.groupBy(rows, (r) => projectOf(r) ?? '');
	const groups = {
		corridors: byProject(corridors, (c) => c.projectId),
		curves: byProject(curves, (c) => projectBySegment.get(c.segmentId)),
		signs: byProject(signs, (s) => projectByCurve.get(s.curveId)),
		signApps: byProject(signApps, (a) => a.projectId),
		barriers: byProject(barriers, (b) => projectByCurve.get(b.curveId)),
		barrierApps: byProject(barrierApps, (a) => a.projectId)
	};
	return projects.map(({ program, project }) => ({
		program,
		project,
		corridors: groups.corridors.get(project.id) ?? [],
		curves: groups.curves.get(project.id) ?? [],
		signs: groups.signs.get(project.id) ?? [],
		signApps: groups.signApps.get(project.id) ?? [],
		barriers: groups.barriers.get(project.id) ?? [],
		barrierApps: groups.barrierApps.get(project.id) ?? []
	}));
}

export const programsMapStore = new ProgramsMapStore();
